import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { ContentCardComponent } from '@shared/components/content-card/content-card.component';
import { MaskService } from '@shared/services/mask.service';
import { NFeEntradaStruct } from '@shared/services/structs/repo-dfe/nfe-entrada.struct';
import {
  classeBadgeStatusNFeEntrada,
  iconeStatusNFeEntrada,
  rotuloStatusNFeEntrada,
} from '@shared/helpers/nota-fiscal-entrada-status.helper';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { SelectPesquisavelService } from '@shared/components/select-pesquisavel/select-pesquisavel.service';
import {
  EstoqueLoteSerieInlineComponent,
  LoteEntradaEstoqueModalItem,
  SerieEntradaEstoqueModalItem,
} from '@shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { UtilService } from '@shared/services/util.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { OrigemAssociacaoItensNFeEntradaEnum } from '@shared/enums/suprimentos/origem-associacao-itens-nfe-entrada.enum';
import { RegraCorrespondenciaProdutoNFeEntradaEnum } from '@shared/enums/suprimentos/regra-correspondencia-produto-nfe-entrada.enum';
import { EstadoAssociacaoItemNFeEntradaEnum } from '@shared/enums/suprimentos/estado-associacao-item-nfe-entrada.enum';
import { TipoItemNFeEntradaEnum } from '@shared/enums/repo-dfe/tipo-item-nfe-entrada.enum';
import { TipoProdutoEnum } from '@shared/enums/produto/tipo-produto.enum';
import { OrigemCadastroProdutoEnum } from '@shared/enums/produto/origem-cadastro-produto.enum';
import { montarUrlCadastroProduto } from '@shared/helpers/produto-cadastro-origem.helper';
import { calcularQuantidadeEstoque } from '@shared/helpers/quantidade-estoque-produto.util';
import { NFeEntradaService } from '@shared/services/apis/repo-dfe/nfe-entrada.service';
import { NFeEntradaItemService } from '@shared/services/apis/repo-dfe/nfe-entrada-item.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { LookupService as ProdutoLookupService } from '@shared/services/apis/produto/produto-lookup.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '@shared/services/requests/pessoa/list-pessoa-ids-request';
import { NFeEntradaItemStruct } from '@shared/services/structs/repo-dfe/nfe-entrada-item.struct';
import { NFeEntradaItemLoteStruct } from '@shared/services/structs/repo-dfe/nfe-entrada-item-lote.struct';
import { ProdutoAssociacaoStruct } from '@shared/services/structs/orquestrador-compras/produto-associacao.struct';
import { ProdutoListStruct } from '@shared/services/structs/produto/produto-list.struct';
import { UpsertCodigoFornecedorRequest } from '@shared/services/requests/orquestrador-compras/upsert-codigo-fornecedor.request';
import { AtualizarNcmProdutoRequest } from '@shared/services/requests/orquestrador-compras/atualizar-ncm-produto.request';
import { AtualizarGtinProdutoRequest } from '@shared/services/requests/orquestrador-compras/atualizar-gtin-produto.request';
import { NFeEntradaAssociacaoItemRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-associacao-item.request';
import { NFeEntradaAssociacaoSalvarRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-associacao-salvar.request';
import {
  NotaFiscalEntradaAssociacaoDialogData,
  NotaFiscalEntradaAssociacaoDialogResult,
  ORDEM_REGRAS_CORRESPONDENCIA_PADRAO,
} from './nota-fiscal-entrada-associacao-dialog-data';

enum FiltroAssociacao {
  Todos = 1,
  Pendentes = 2,
  Associados = 3,
}

enum SituacaoEan {
  NaoCadastrado = 1,
  Divergente = 2,
}

enum TipoControleEstoque {
  Lote = 1,
  Serie = 2,
}

/** Atualização do cadastro do produto em andamento no item: o spinner aparece só no botão dela. */
enum OperacaoCadastro {
  Ncm = 1,
  Ean = 2,
  CodigoFornecedor = 3,
  ConversaoSalva = 4,
}

const VISUAL_POR_REGRA: Record<RegraCorrespondenciaProdutoNFeEntradaEnum, { rotulo: string; tooltip: string; icone: string; classe: string }> = {
  [RegraCorrespondenciaProdutoNFeEntradaEnum.Ean]: { rotulo: 'GTIN/EAN', tooltip: 'Relacionado pelo GTIN/EAN do produto', icone: 'qr_code_2', classe: 'badge-ciano' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigoDescricao]: { rotulo: 'Forn.+cód.+descrição', tooltip: 'Fornecedor + código no fabricante + descrição', icone: 'verified', classe: 'badge-azul' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigo]: { rotulo: 'Forn.+cód. fabricante', tooltip: 'Fornecedor + código no fabricante', icone: 'badge', classe: 'badge-indigo' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.CodigoDescricao]: { rotulo: 'Cód.+descrição', tooltip: 'Código no fabricante + descrição', icone: 'tag', classe: 'badge-teal' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.Descricao]: { rotulo: 'Só descrição', tooltip: 'Relacionado apenas pela descrição - confira o vínculo', icone: 'match_case', classe: 'badge-amarelo' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.Manual]: { rotulo: 'Escolha manual', tooltip: 'Produto escolhido na busca do cadastro', icone: 'touch_app', classe: 'badge-cinza' },
  [RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia]: { rotulo: 'Sem correspondência', tooltip: 'Nenhuma regra encontrou produto no cadastro', icone: 'error', classe: 'badge-vermelho' },
};

class ItemAssociacao {
  public item: NFeEntradaItemStruct;
  public estado = EstadoAssociacaoItemNFeEntradaEnum.Pendente;
  public regra = RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia;
  public produto: ProdutoAssociacaoStruct | null = null;
  public agrupador: ProdutoAssociacaoStruct | null = null;
  public filhosVariacao: ProdutoAssociacaoStruct[] = [];
  public isExpandido = true;
  public isBuscandoSugestao = false;
  public isCarregandoFilhos = false;
  public isDestacado = false;
  public isAssociacaoDesfeita = false;
  public produtoControl = new FormControl<number | null>(null);
  public fator: number | null = null;
  public isFatorConfirmado = false;
  public isFatorDoCadastro = false;
  /** Os lotes ainda estão na unidade da nota (vieram do XML); a conversão para a de estoque só ocorre enquanto verdadeiro. */
  public isLotesNaUnidadeComercial = false;
  public lotes: NFeEntradaItemLoteStruct[] = [];
  public numerosSerie: string[] = [];
  public itensLote: LoteEntradaEstoqueModalItem[] = [];
  public itensSerie: SerieEntradaEstoqueModalItem[] = [];
  public isLoteSerieAberto = false;
  public decisaoNcm: string | null = null;
  public ncmCadastroDecisao: string | null = null;
  public decisaoEan: string | null = null;
  public situacaoEanDecisao: SituacaoEan | null = null;
  public eanCadastroDecisao: string | null = null;
  public decisaoCodigo: string | null = null;
  public isCodigoFornecedorIgnorado = false;
  public isCodigoFornecedorRegistrado = false;
  public operacaoCadastro: OperacaoCadastro | null = null;
  /** Alterado desde a última gravação: só estes itens vão para a API, os demais não são sobrescritos. */
  public isAlterado = false;
  /** Muda a cada troca de produto: resposta assíncrona pedida em outra versão é descartada. */
  public versao = 0;
  /** O produto gravado no item não existe mais no cadastro (diferente de cadastro indisponível). */
  public isProdutoNaoEncontrado = false;
  public isErroSugestao = false;
  public isErroFilhos = false;

  get isAtualizandoCadastro(): boolean { return this.operacaoCadastro !== null; }
}

/** Aprendizado do código do fornecedor que falhou depois da associação gravada: repete só ele. */
class AprendizadoPendente {
  public itens: ItemAssociacao[] = [];
  public resultado = new NotaFiscalEntradaAssociacaoDialogResult();
}

function normalizar(texto: string | null | undefined): string {
  return (texto ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

function somenteDigitos(texto: string | null | undefined): string {
  return (texto ?? '').replace(/\D/g, '');
}

/** Heurística simples de semelhança: alguma palavra de 4+ letras em comum entre as duas descrições. */
function descricoesSemelhantes(a: string, b: string): boolean {
  const palavrasA = new Set(normalizar(a).split(/\s+/).filter(p => p.length >= 4));
  return normalizar(b).split(/\s+/).filter(p => p.length >= 4).some(p => palavrasA.has(p));
}

function paraDataIso(data: Date | string | null): string | undefined {
  if (!data) return undefined;
  const d = new Date(data);
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

function produtoDaLista(p: ProdutoListStruct): ProdutoAssociacaoStruct {
  const produto = new ProdutoAssociacaoStruct();
  produto.idProduto = p.idProduto;
  produto.descricao = p.descricao;
  produto.codigoSku = p.codigoSku ?? null;
  produto.gtinEan = p.gtinEan ?? null;
  produto.ncm = p.ncm ?? null;
  produto.unidade = p.unidade ?? null;
  produto.idUnidadeMedida = p.idUnidadeMedida ?? null;
  produto.isControlaLotes = p.isControlaLotes;
  produto.isControlaSeries = p.isControlaSeries;
  produto.idTipoProduto = p.idTipoProduto;
  produto.isAgrupadorVariacao = p.idTipoProduto === TipoProdutoEnum.ComVariacao;
  produto.isUnidadeMedidaFracionada = p.isUnidadeMedidaFracionada ?? true;
  return produto;
}

/**
 * Modal de associação dos itens da NF-e de entrada com os produtos do cadastro: roda as regras de
 * correspondência na ordem configurada, permite trocar/buscar/cadastrar o produto, confirmar o
 * fator de conversão, informar lote/série e resolver divergências de NCM, EAN e código do fornecedor.
 * As regras de confirmação são as mesmas do OrquestradorCompras (AssociacaoNFeEntradaHelper).
 */
@Component({
  selector: 'app-nota-fiscal-entrada-associacao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    NgxCurrencyDirective,
    MatProgressSpinnerModule,
    SelectPesquisavelComponent,
    ContentCardComponent,
    AlertCardComponent,
    EmptyStateComponent,
    LoadingBoxComponent,
    EstoqueLoteSerieInlineComponent,
    AbntPipe,
  ],
  templateUrl: './nota-fiscal-entrada-associacao-dialog.component.html',
  styleUrls: ['./nota-fiscal-entrada-associacao-dialog.component.css'],
})
export class NotaFiscalEntradaAssociacaoDialogComponent implements OnInit {
  readonly filtroEnum = FiltroAssociacao;
  readonly estadoEnum = EstadoAssociacaoItemNFeEntradaEnum;
  readonly situacaoEanEnum = SituacaoEan;
  readonly tipoControleEnum = TipoControleEstoque;
  readonly operacaoCadastroEnum = OperacaoCadastro;
  readonly urlProduto = `${environment.urlApiProduto}Lookup/listProdutoSelect`;

  itens: ItemAssociacao[] = [];
  filtro = FiltroAssociacao.Todos;
  isLoading = true;
  /** A nota ou os itens não carregaram: nada é exibido para editar. */
  isErroCarga = false;
  /** Os produtos já associados não carregaram: salvar e concluir ficam bloqueados até nova tentativa. */
  isErroCargaProdutos = false;
  isSalvando = false;
  isConcluindo = false;
  isMaximizado = true;
  temAlteracoes = false;
  aprendizadoPendente: AprendizadoPendente | null = null;
  isRepetindoAprendizado = false;

  nota: NFeEntradaStruct | null = null;
  readonly classeBadgeStatus = classeBadgeStatusNFeEntrada;
  readonly iconeStatus = iconeStatusNFeEntrada;
  readonly rotuloStatus = rotuloStatusNFeEntrada;
  readonly fatorMask;

  numeroNota: string | null = null;
  serieNota: string | null = null;
  nomeFornecedor: string | null = null;
  dataEmissao: Date | null = null;
  valorTotal: number | null = null;
  chaveAcesso: string | null = null;
  idPessoaEmitente: number | null = null;
  datetimeAlteracaoNFeEntrada: Date | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NotaFiscalEntradaAssociacaoDialogData,
    private dialogRef: MatDialogRef<NotaFiscalEntradaAssociacaoDialogComponent, NotaFiscalEntradaAssociacaoDialogResult>,
    private dialog: MatDialog,
    private nfeEntradaService: NFeEntradaService,
    private nfeEntradaItemService: NFeEntradaItemService,
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private produtoLookupService: ProdutoLookupService,
    private pessoaLookupService: PessoaLookupService,
    private selectPesquisavelService: SelectPesquisavelService,
    private alertService: AlertService,
    private maskService: MaskService,
    private authService: AuthService) {
    this.fatorMask = this.maskService.getDynamicDecimalMask(null, false);
    this.dialogRef.updateSize('100vw', '100vh');
    this.dialogRef.addPanelClass('associacao-dialog-maximizado');
  }

  ngOnInit(): void {
    this.carregar();
  }

  // ---------------------------------------------------------------- contadores e filtro

  get quantidadeTotal(): number { return this.itens.length; }
  get quantidadeResolvidos(): number { return this.itens.filter(x => this.isResolvido(x)).length; }
  get percentualResolvidos(): number { return this.quantidadeTotal ? (this.quantidadeResolvidos / this.quantidadeTotal) * 100 : 0; }
  get isTodosAssociados(): boolean {
    return this.quantidadeTotal > 0 && this.itens.every(x => this.isResolvido(x));
  }
  get itensFiltrados(): ItemAssociacao[] {
    if (this.filtro === FiltroAssociacao.Associados) return this.itens.filter(x => this.isResolvido(x));
    if (this.filtro === FiltroAssociacao.Pendentes) return this.itens.filter(x => !this.isResolvido(x));
    return this.itens;
  }
  get exibirAcessarNota(): boolean { return this.data.origem !== OrigemAssociacaoItensNFeEntradaEnum.EdicaoNota; }
  /** Origem usada somente nas ações de saída e nos textos de direcionamento do modal. */
  get isKanban(): boolean { return this.data.origem === OrigemAssociacaoItensNFeEntradaEnum.KanbanVinculo; }
  get isGravando(): boolean { return this.isSalvando || this.isConcluindo || this.isRepetindoAprendizado; }
  get isAtualizandoCadastroItem(): boolean { return this.itens.some(x => x.isAtualizandoCadastro); }
  get textoItensAprendizadoPendente(): string {
    return (this.aprendizadoPendente?.itens ?? []).map(x => `#${x.item.numeroItem}`).join(', ');
  }

  selecionarFiltro(filtro: FiltroAssociacao): void { this.filtro = filtro; }

  // ---------------------------------------------------------------- estado do item

  isResolvido(i: ItemAssociacao): boolean {
    return i.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.podeConfirmar(i);
  }

  /** Mesmas regras do OrquestradorCompras na confirmação individual, coletiva e na conclusão. */
  podeConfirmar(i: ItemAssociacao): boolean {
    return !!i.produto && !(i.agrupador && !i.filhosVariacao.some(f => f.idProduto === i.produto?.idProduto))
      && !this.isFatorPendente(i) && !this.isQuantidadeFracionadaInvalida(i) && !this.isLoteSeriePendente(i);
  }

  motivoNaoConfirma(i: ItemAssociacao): string {
    if (i.agrupador && !i.filhosVariacao.some(f => f.idProduto === i.produto?.idProduto))
      return 'Escolha a variação que recebe a entrada';
    return this.motivoPendencia(i);
  }

  motivoPendencia(i: ItemAssociacao): string {
    if (i.isProdutoNaoEncontrado) return 'O produto associado não existe mais no cadastro. Escolha outro produto.';
    if (!i.produto) return 'Selecione o produto do sistema correspondente a este item da nota.';
    if (this.isFatorPendente(i)) return `Confirme o fator de conversão entre ${i.item.unidadeComercial} e ${i.produto.unidade}.`;
    if (this.isQuantidadeFracionadaInvalida(i))
      return `A unidade ${i.produto.unidade} não aceita quantidade fracionada (${this.quantidadeEstoque(i)}). Revise o fator de conversão.`;
    if (this.isLoteSeriePendente(i))
      return this.tipoControle(i) === TipoControleEstoque.Serie
        ? `Informe ${this.quantidadeEstoque(i)} número(s) de série distintos.`
        : `Informe lotes completos que somem ${this.quantidadeEstoque(i)} ${i.produto.unidade}.`;
    if (i.estado !== EstadoAssociacaoItemNFeEntradaEnum.Associado) return 'Confirme a associação deste produto ao item da nota.';
    return '';
  }

  visualRegra(i: ItemAssociacao) { return VISUAL_POR_REGRA[i.regra]; }

  exibirBadgeRegra(i: ItemAssociacao): boolean {
    return !!i.produto || i.regra === RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia;
  }

  isRegraDescricao(i: ItemAssociacao): boolean { return i.regra === RegraCorrespondenciaProdutoNFeEntradaEnum.Descricao; }

  metaItem(i: ItemAssociacao): string {
    return [
      i.item.codigoProdutoEmitente && `Cód. ${i.item.codigoProdutoEmitente}`,
      i.item.codigoEanComercial && `EAN ${i.item.codigoEanComercial}`,
      i.item.codigoNcm && `NCM ${i.item.codigoNcm}`,
    ].filter(Boolean).join(' · ') || '-';
  }

  metaProduto(p: ProdutoAssociacaoStruct): string {
    return [p.codigoSku, p.unidade && `estoque em ${p.unidade}`, p.ncm && `NCM ${p.ncm}`].filter(Boolean).join(' · ');
  }

  descricaoControle(p: ProdutoAssociacaoStruct): string {
    if (p.isControlaSeries) return 'Controla nº de série';
    if (p.isControlaLotes) return 'Controla lote + validade';
    return 'Sem controle de lote/série';
  }

  isFabricado(i: ItemAssociacao): boolean { return i.produto?.idTipoProduto === TipoProdutoEnum.Fabricado; }

  toggleExpandido(i: ItemAssociacao): void { i.isExpandido = !i.isExpandido; }

  /** Alteração que precisa ir para a API; a ação do usuário também pede confirmação ao descartar. */
  private marcarAlterado(i: ItemAssociacao, isAcaoUsuario = true): void {
    i.isAlterado = true;
    if (isAcaoUsuario) this.temAlteracoes = true;
  }

  // ---------------------------------------------------------------- fator de conversão

  isUnidadeDiferente(i: ItemAssociacao): boolean {
    return !!i.produto && normalizar(i.item.unidadeComercial) !== normalizar(i.produto.unidade);
  }

  isFatorPendente(i: ItemAssociacao): boolean {
    return this.isUnidadeDiferente(i) && !i.isFatorConfirmado;
  }

  isQuantidadeFracionadaInvalida(i: ItemAssociacao): boolean {
    return !!i.produto && !i.produto.isUnidadeMedidaFracionada && !this.isFatorPendente(i) && this.quantidadeEstoque(i) % 1 !== 0;
  }

  /** Quantidade do item com as casas do próprio valor (item do XML não é truncado; mínimo da empresa, máximo 4). */
  casasQuantidade(valor: number | null): number {
    return UtilService.casasDecimaisDoValor(valor, this.authService.getUserInfos()?.casasDecimaisQuantidade || 2, 4);
  }

  /** Mesmo critério da quantidade para o unitário (máximo 10). */
  casasValorUnitario(valor: number | null): number {
    return UtilService.casasDecimaisDoValor(valor, this.authService.getUserInfos()?.casasDecimaisPreco || 2, 10);
  }

  quantidadeEstoque(i: ItemAssociacao): number {
    return calcularQuantidadeEstoque(i.item.quantidadeComercial ?? 0, i.fator, i.item.unidadeComercial, i.produto?.unidade);
  }

  siglaComFator(i: ItemAssociacao): RegExpMatchArray | null {
    return (i.item.unidadeComercial ?? '').toUpperCase().match(/^([A-Z]+)(\d+)$/);
  }

  textoHistoricoFator(i: ItemAssociacao): string {
    if (i.isFatorDoCadastro)
      return `Fator ${i.item.unidadeComercial} = ${i.fator} ${i.produto?.unidade} salvo no cadastro do fornecedor × produto.`;
    const sigla = this.siglaComFator(i);
    if (sigla)
      return `Fator inferido pela sigla da unidade ${sigla[0]} - confirme, porque é ele que define a quantidade que entra no estoque.`;
    return 'Primeira compra deste produto com este fornecedor - confirme o fator, porque é ele que define a quantidade que entra no estoque.';
  }

  alterarFator(i: ItemAssociacao, valor: number | null): void {
    if (this.data.isSomenteLeitura || i.isFatorConfirmado) return;
    i.fator = valor && valor > 0 ? valor : null;
    this.marcarAlterado(i);
  }

  confirmarFator(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || i.isFatorConfirmado) return;
    if (!i.fator || i.fator <= 0) {
      this.alertService.show('Aviso', 'Informe um fator de conversão maior que zero.', AlertType.warning);
      return;
    }
    i.isFatorConfirmado = true;
    this.converterLotesDaUnidadeComercial(i);
    this.marcarAlterado(i);
    if (i.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isResolvido(i)) this.focarProximoPendente(i);
  }

  ajustarFator(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura) return;
    i.isFatorConfirmado = false;
    i.isFatorDoCadastro = false;
    this.marcarAlterado(i);
  }

  /** Apaga o fator memorizado no vínculo produto × fornecedor e pede um novo fator para o item. */
  limparConversaoSalva(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || !i.produto || !this.idPessoaEmitente || !i.item.codigoProdutoEmitente?.trim() || i.isAtualizandoCadastro) return;
    const versao = i.versao;
    const request = new UpsertCodigoFornecedorRequest();
    request.idProduto = i.produto.idProduto;
    request.idFornecedor = this.idPessoaEmitente;
    request.codigo = i.item.codigoProdutoEmitente.trim();
    request.unidadeMedida = i.item.unidadeComercial;
    request.fatorConversao = null;
    i.operacaoCadastro = OperacaoCadastro.ConversaoSalva;
    this.notaFiscalEntradaService.upsertCodigoFornecedor(request).pipe(catchError(() => of(null))).subscribe(response => {
      i.operacaoCadastro = null;
      if (!response || i.versao !== versao) return;
      if (response.isError) { this.alertService.show('Aviso', response.errorDescription, AlertType.warning); return; }
      i.produto!.fatorConversaoFornecedor = null;
      i.isFatorDoCadastro = false;
      i.isFatorConfirmado = false;
      i.fator = null;
      this.marcarAlterado(i);
      this.alertService.show('Fator removido', 'Fator salvo removido - confirme o novo fator para este item.', AlertType.info);
    });
  }

  /**
   * Unidades iguais valem 1. Senão, o fator gravado na nota vem antes do histórico do fornecedor e
   * mantém a confirmação gravada; o do fornecedor pede confirmação. Sem nenhum dos dois, pede o fator.
   */
  private definirFator(i: ItemAssociacao, fatorFornecedor: number | null | undefined, fatorSalvo: number | null, isFatorSalvoConfirmado: boolean): void {
    i.isFatorDoCadastro = false;
    if (!this.isUnidadeDiferente(i)) {
      i.fator = 1;
      i.isFatorConfirmado = true;
    } else if (fatorSalvo && fatorSalvo > 0) {
      i.fator = fatorSalvo;
      i.isFatorConfirmado = isFatorSalvoConfirmado;
    } else if (fatorFornecedor && fatorFornecedor > 0) {
      i.fator = fatorFornecedor;
      i.isFatorConfirmado = false;
      i.isFatorDoCadastro = true;
    } else {
      const sigla = this.siglaComFator(i);
      i.fator = sigla ? Number(sigla[2]) : null;
      i.isFatorConfirmado = false;
    }
    if (i.isFatorConfirmado) this.converterLotesDaUnidadeComercial(i);
  }

  /**
   * Os lotes do XML (rastro) vêm na unidade da nota; lote/série são informados na unidade de estoque.
   * Converte uma única vez: depois disso o item grava que os lotes já estão na unidade de estoque.
   */
  private converterLotesDaUnidadeComercial(i: ItemAssociacao): void {
    if (!i.isLotesNaUnidadeComercial || !i.fator) return;
    i.isLotesNaUnidadeComercial = false;
    this.marcarAlterado(i, false);
    if (i.fator === 1) return;
    i.lotes = i.lotes.map(l => ({
      ...l,
      quantidadeLote: calcularQuantidadeEstoque(l.quantidadeLote, i.fator, i.item.unidadeComercial, i.produto?.unidade),
    }));
    this.montarItensLoteSerie(i);
  }

  // ---------------------------------------------------------------- lote e série

  tipoControle(i: ItemAssociacao): TipoControleEstoque | null {
    if (i.produto?.isControlaSeries) return TipoControleEstoque.Serie;
    if (i.produto?.isControlaLotes) return TipoControleEstoque.Lote;
    return null;
  }

  isLoteSeriePendente(i: ItemAssociacao): boolean {
    const tipo = this.tipoControle(i);
    const alvo = this.quantidadeEstoque(i);
    if (tipo === TipoControleEstoque.Serie) {
      const series = i.numerosSerie.map(s => s.trim().toUpperCase()).filter(Boolean);
      return series.length !== alvo || new Set(series).size !== series.length;
    }
    if (tipo === TipoControleEstoque.Lote) {
      const soma = i.lotes.reduce((total, l) => total + (l.quantidadeLote || 0), 0);
      const isIncompleto = i.lotes.some(l => !l.numeroLote?.trim() || !(l.quantidadeLote > 0) || !l.dataFabricacao || !l.dataValidade);
      return i.lotes.length === 0 || isIncompleto || Math.abs(soma - alvo) > 0.0001;
    }
    return false;
  }

  /** Digitação manual no painel acontece na unidade de estoque. */
  alterarLoteSerie(i: ItemAssociacao, itens: (LoteEntradaEstoqueModalItem | SerieEntradaEstoqueModalItem)[]): void {
    if (this.data.isSomenteLeitura) return;
    if (this.tipoControle(i) === TipoControleEstoque.Serie) {
      i.numerosSerie = (itens as SerieEntradaEstoqueModalItem[]).map(s => s.codigoSerie);
    } else {
      i.lotes = (itens as LoteEntradaEstoqueModalItem[]).map(l => ({
        numeroLote: l.codigoLote,
        quantidadeLote: l.quantidade,
        dataFabricacao: l.dataFabricacao ? new Date(l.dataFabricacao + 'T00:00:00') : null,
        dataValidade: l.dataValidade ? new Date(l.dataValidade + 'T00:00:00') : null,
      }));
    }
    i.isLotesNaUnidadeComercial = false;
    this.marcarAlterado(i);
    if (i.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isResolvido(i)) this.focarProximoPendente(i);
  }

  private montarItensLoteSerie(i: ItemAssociacao): void {
    i.itensLote = i.lotes.map(l => ({
      codigoLote: l.numeroLote,
      quantidade: l.quantidadeLote,
      dataFabricacao: paraDataIso(l.dataFabricacao),
      dataValidade: paraDataIso(l.dataValidade),
    }));
    i.itensSerie = i.numerosSerie.map(codigoSerie => ({ codigoSerie }));
  }

  // ---------------------------------------------------------------- divergências com o cadastro

  isNcmDivergente(i: ItemAssociacao): boolean {
    const ncmNota = somenteDigitos(i.item.codigoNcm);
    const ncmProduto = somenteDigitos(i.produto?.ncm);
    return !!i.produto && ncmNota.length > 0 && ncmProduto.length > 0 && ncmNota !== ncmProduto;
  }

  situacaoEan(i: ItemAssociacao): SituacaoEan | null {
    const eanNota = somenteDigitos(i.item.codigoEanComercial);
    if (!i.produto || eanNota.length < 8 || i.regra === RegraCorrespondenciaProdutoNFeEntradaEnum.Ean) return null;
    const eanProduto = somenteDigitos(i.produto.gtinEan);
    if (!eanProduto) return SituacaoEan.NaoCadastrado;
    return eanProduto !== eanNota ? SituacaoEan.Divergente : null;
  }

  isCodigoFornecedorNaoRegistrado(i: ItemAssociacao): boolean {
    return !!i.produto && !!i.item.codigoProdutoEmitente?.trim() && !!this.idPessoaEmitente
      && i.regra !== RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigo
      && i.regra !== RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigoDescricao;
  }

  /** Depois da decisão a barra continua visível, em verde, com o texto do momento em que foi decidida. */
  exibirDivergenciaNcm(i: ItemAssociacao): boolean { return !!i.decisaoNcm || this.isNcmDivergente(i); }

  ncmCadastro(i: ItemAssociacao): string | null { return i.decisaoNcm ? i.ncmCadastroDecisao : i.produto?.ncm ?? null; }

  situacaoEanExibida(i: ItemAssociacao): SituacaoEan | null { return i.decisaoEan ? i.situacaoEanDecisao : this.situacaoEan(i); }

  eanCadastro(i: ItemAssociacao): string | null { return i.decisaoEan ? i.eanCadastroDecisao : i.produto?.gtinEan ?? null; }

  temFaixaInferior(i: ItemAssociacao): boolean {
    return !!i.agrupador || (!!i.produto && (this.exibirDivergenciaNcm(i) || this.situacaoEanExibida(i) !== null
      || this.isCodigoFornecedorNaoRegistrado(i) || this.isFabricado(i) || this.isUnidadeDiferente(i) || this.tipoControle(i) !== null));
  }

  manterNcm(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura) return;
    i.ncmCadastroDecisao = i.produto?.ncm ?? null;
    i.decisaoNcm = 'cadastro mantido';
  }

  atualizarNcm(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || !i.produto || i.isAtualizandoCadastro) return;
    const versao = i.versao;
    const produto = i.produto;
    const request = new AtualizarNcmProdutoRequest();
    request.idProduto = produto.idProduto;
    request.ncm = somenteDigitos(i.item.codigoNcm);
    i.operacaoCadastro = OperacaoCadastro.Ncm;
    this.notaFiscalEntradaService.atualizarNcmProduto(request).pipe(catchError(() => of(null))).subscribe(response => {
      i.operacaoCadastro = null;
      if (!response || i.versao !== versao || i.produto !== produto) return;
      if (response.isError) { this.alertService.show('Aviso', response.errorDescription, AlertType.warning); return; }
      i.ncmCadastroDecisao = produto.ncm;
      produto.ncm = request.ncm;
      i.decisaoNcm = 'cadastro atualizado';
    });
  }

  ignorarEan(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura) return;
    i.situacaoEanDecisao = this.situacaoEan(i);
    i.eanCadastroDecisao = i.produto?.gtinEan ?? null;
    i.decisaoEan = i.situacaoEanDecisao === SituacaoEan.Divergente ? 'cadastro mantido' : 'EAN ignorado';
  }

  gravarEan(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || !i.produto || i.isAtualizandoCadastro) return;
    const versao = i.versao;
    const produto = i.produto;
    const situacao = this.situacaoEan(i);
    const isDivergente = situacao === SituacaoEan.Divergente;
    const request = new AtualizarGtinProdutoRequest();
    request.idProduto = produto.idProduto;
    request.gtinEan = somenteDigitos(i.item.codigoEanComercial);
    i.operacaoCadastro = OperacaoCadastro.Ean;
    this.notaFiscalEntradaService.atualizarGtinProduto(request).pipe(catchError(() => of(null))).subscribe(response => {
      i.operacaoCadastro = null;
      if (!response || i.versao !== versao || i.produto !== produto) return;
      if (response.isError) { this.alertService.show('Aviso', response.errorDescription, AlertType.warning); return; }
      i.situacaoEanDecisao = situacao;
      i.eanCadastroDecisao = produto.gtinEan;
      produto.gtinEan = request.gtinEan;
      i.decisaoEan = isDivergente ? 'cadastro atualizado' : 'EAN adicionado ao produto';
    });
  }

  ignorarCodigoFornecedor(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura) return;
    i.isCodigoFornecedorIgnorado = true;
    i.decisaoCodigo = 'ignorado';
  }

  registrarCodigoFornecedor(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || !i.produto || i.isAtualizandoCadastro) return;
    const versao = i.versao;
    i.operacaoCadastro = OperacaoCadastro.CodigoFornecedor;
    this.enviarCodigoFornecedor(i).subscribe(ok => {
      i.operacaoCadastro = null;
      if (!ok || i.versao !== versao) return;
      i.isCodigoFornecedorRegistrado = true;
      i.decisaoCodigo = 'código registrado';
    });
  }

  private enviarCodigoFornecedor(i: ItemAssociacao): Observable<boolean> {
    const request = new UpsertCodigoFornecedorRequest();
    request.idProduto = i.produto!.idProduto;
    request.idFornecedor = this.idPessoaEmitente!;
    request.codigo = i.item.codigoProdutoEmitente!.trim();
    request.unidadeMedida = i.item.unidadeComercial;
    request.fatorConversao = i.isFatorConfirmado ? i.fator : null;
    return this.notaFiscalEntradaService.upsertCodigoFornecedor(request).pipe(
      map(response => {
        if (response.isError) this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
        return !response.isError;
      }),
      catchError(() => of(false)),
    );
  }

  // ---------------------------------------------------------------- troca, busca e cadastro de produto

  abrirBusca(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || i.isAtualizandoCadastro || i.isBuscandoSugestao) return;
    // A troca fica pendente também no payload; lote/série só serão usados após nova associação.
    i.versao++;
    i.produto = null;
    i.agrupador = null;
    i.filhosVariacao = [];
    i.isErroFilhos = false;
    i.produtoControl.reset(null, { emitEvent: false });
    i.estado = EstadoAssociacaoItemNFeEntradaEnum.Pendente;
    i.regra = RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia;
    i.isAssociacaoDesfeita = true;
    this.marcarAlterado(i);
  }

  escolherProduto(i: ItemAssociacao, p: ProdutoListStruct): void {
    if (this.data.isSomenteLeitura || !p?.idProduto) return;
    i.isAssociacaoDesfeita = false;
    i.isProdutoNaoEncontrado = false;
    i.isErroSugestao = false;
    i.regra = RegraCorrespondenciaProdutoNFeEntradaEnum.Manual;
    this.aplicarProduto(i, produtoDaLista(p), null, false);
    this.marcarAlterado(i);
    if (!i.agrupador && this.podeConfirmar(i)) {
      this.confirmarItem(i);
      this.alertService.show('Sucesso', `Produto ${p.descricao} associado ao item #${i.item.numeroItem}.`, AlertType.success);
    } else {
      i.estado = EstadoAssociacaoItemNFeEntradaEnum.Pendente;
      i.isExpandido = true;
    }
  }

  cadastrarProduto(i: ItemAssociacao): void {
    if (this.data.isSomenteLeitura || !i.item.idNFeEntradaItem) return;
    window.open(montarUrlCadastroProduto(OrigemCadastroProdutoEnum.NFeEntradaItem, i.item.idNFeEntradaItem), '_blank', 'noopener');
  }

  // ---------------------------------------------------------------- variação

  escolherVariacao(i: ItemAssociacao, filho: ProdutoAssociacaoStruct): void {
    if (this.data.isSomenteLeitura || i.isAtualizandoCadastro) return;
    this.aplicarProduto(i, filho, null, false, true);
    this.marcarAlterado(i);
  }

  isVariacaoSelecionada(i: ItemAssociacao, filho: ProdutoAssociacaoStruct): boolean {
    return i.produto?.idProduto === filho.idProduto;
  }

  recarregarVariacoes(i: ItemAssociacao): void {
    if (i.agrupador) this.carregarFilhosVariacao(i, i.agrupador.idProduto);
  }

  // ---------------------------------------------------------------- confirmação

  confirmarItem(i: ItemAssociacao): void {
    if (!this.podeConfirmar(i) || this.data.isSomenteLeitura || this.isGravando || this.isLoading) return;
    i.estado = EstadoAssociacaoItemNFeEntradaEnum.Associado;
    this.marcarAlterado(i);
    if (!this.isResolvido(i)) return;
    i.isExpandido = false;
    this.focarProximoPendente(i);
  }

  private focarProximoPendente(atual: ItemAssociacao): void {
    const indice = this.itens.indexOf(atual);
    const proximo = [...this.itens.slice(indice + 1), ...this.itens.slice(0, indice)].find(x => !this.isResolvido(x));
    if (proximo) setTimeout(() => this.destacarItem(proximo), 280);
  }

  private destacarItem(i: ItemAssociacao): void {
    i.isExpandido = true;
    i.isDestacado = true;
    setTimeout(() => document.getElementById(`associacao-item-${i.item.numeroItem}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    setTimeout(() => (i.isDestacado = false), 1600);
  }

  /** Confirma somente itens que já têm produto e cumprem as regras; o cadastro ocorre na tela completa. */
  confirmarTodos(): void {
    if (this.data.isSomenteLeitura || this.isGravando) return;
    this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Pendente && this.podeConfirmar(x))
      .forEach(x => {
        x.estado = EstadoAssociacaoItemNFeEntradaEnum.Associado;
        this.marcarAlterado(x);
      });
    this.itens.filter(x => this.isResolvido(x)).forEach(x => (x.isExpandido = false));
  }

  /** Salvar e concluir só seguem com a carga completa e sem atualização de cadastro em andamento. */
  private podeGravar(): boolean {
    if (this.isErroCargaProdutos) {
      this.alertService.show('Produtos não carregados',
        'Os produtos já associados não foram carregados. Tente carregar novamente antes de salvar.', AlertType.warning);
      return false;
    }
    // Atualização de cadastro em andamento: os botões ficam desabilitados até ela terminar.
    if (this.isAtualizandoCadastroItem) return false;
    return true;
  }

  concluir(): void {
    if (this.data.isSomenteLeitura || this.isGravando || this.isLoading || !this.podeGravar()) return;
    const comFatorPendente = this.itens.find(x => this.isFatorPendente(x));
    if (comFatorPendente) {
      this.alertService.show('Fator de conversão pendente',
        `Confirme o fator de conversão do item #${comFatorPendente.item.numeroItem} antes de concluir.`, AlertType.warning);
      this.destacarItem(comFatorPendente);
      return;
    }

    const comLoteSeriePendente = this.itens.filter(x => this.isLoteSeriePendente(x));
    if (comLoteSeriePendente.length) {
      const n = comLoteSeriePendente.length;
      this.alertService.show('Lote/série pendente',
        `${n} ${n === 1 ? 'item ainda está sem lote/série' : 'itens ainda estão sem lote/série'} - informe para poder concluir a associação.`,
        AlertType.warning);
      this.destacarItem(comLoteSeriePendente[0]);
      return;
    }
    if (!this.isTodosAssociados) return;

    this.isConcluindo = true;
    this.notaFiscalEntradaService.confirmarTodasAssociacoes(this.montarRequest()).pipe(catchError(() => of(null))).subscribe(response => {
      this.isConcluindo = false;
      if (!response) return;
      if (response.isError && !response.isAssociacaoSalva) {
        this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
        return;
      }

      const resultado = new NotaFiscalEntradaAssociacaoDialogResult();
      resultado.salvo = true;
      if (response.isError) {
        // Associação gravada; a reconferência falhou e a divergência se atualiza na próxima conferência da nota.
        this.alertService.show('Associação salva',
          `A associação foi salva, mas a conferência da nota não pôde ser atualizada: ${response.errorDescription}`, AlertType.warning);
      } else if (response.possuiDivergenciaBloqueante) {
        resultado.possuiDivergenciaBloqueante = true;
        this.alertService.show('Divergência gerada',
          'Associação concluída. Resolva as divergências bloqueantes e confirme o lançamento na Gestão de Nota.', AlertType.warning);
      } else {
        resultado.confirmado = true;
        this.alertService.show('Sucesso', 'Associação concluída. Confirme o lançamento na Gestão de Nota.', AlertType.success);
      }
      this.registrarAprendizadoEFechar(resultado, () => this.dialogRef.close(resultado));
    });
  }

  salvar(): void {
    if (this.data.isSomenteLeitura || this.isGravando || this.isLoading || !this.podeGravar()) return;
    if (!this.itens.some(x => x.isAlterado)) {
      this.dialogRef.close();
      return;
    }

    this.isSalvando = true;
    this.notaFiscalEntradaService.salvarAssociacoes(this.montarRequest()).pipe(catchError(() => of(null))).subscribe(response => {
      this.isSalvando = false;
      if (!response) return;
      if (response.isError) { this.alertService.show('Aviso', response.errorDescription, AlertType.warning); return; }

      const resultado = new NotaFiscalEntradaAssociacaoDialogResult();
      resultado.salvo = true;
      this.registrarAprendizadoEFechar(resultado, () => {
        if (this.isKanban) {
          this.fecharSalvoNoKanban();
          return;
        }
        this.alertService.show('Associação salva',
          `${this.quantidadeResolvidos} de ${this.quantidadeTotal} itens associados. Retome por Ações › Continuar associação de itens.`,
          AlertType.info);
        this.dialogRef.close(resultado);
      });
    });
  }

  /**
   * Aprendizado: o vínculo fornecedor × código × produto (com unidade e fator) é gravado para todo
   * item associado, para a regra por código do fornecedor achar o produto nas próximas notas. Roda
   * depois da associação gravada e separado dela: falha não desfaz a associação; o modal lista os
   * itens que falharam e repete só eles, sem gravar a associação de novo.
   */
  private registrarAprendizadoEFechar(resultado: NotaFiscalEntradaAssociacaoDialogResult, fechar: () => void): void {
    const itens = this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && !!x.produto && !!this.idPessoaEmitente
      && !!x.item.codigoProdutoEmitente?.trim() && !x.isCodigoFornecedorIgnorado && !x.isCodigoFornecedorRegistrado);
    this.enviarAprendizado(itens).subscribe(falhos => {
      if (!falhos.length) {
        fechar();
        return;
      }
      this.aprendizadoPendente = new AprendizadoPendente();
      this.aprendizadoPendente.itens = falhos;
      this.aprendizadoPendente.resultado = resultado;
      this.atualizarVersoes();
    });
  }

  repetirAprendizado(): void {
    if (!this.aprendizadoPendente || this.isRepetindoAprendizado) return;
    this.isRepetindoAprendizado = true;
    this.enviarAprendizado(this.aprendizadoPendente.itens).subscribe(falhos => {
      this.isRepetindoAprendizado = false;
      if (!this.aprendizadoPendente) return;
      if (falhos.length) {
        this.aprendizadoPendente.itens = falhos;
        return;
      }
      const resultado = this.aprendizadoPendente.resultado;
      this.aprendizadoPendente = null;
      this.alertService.show('Sucesso', 'Códigos do fornecedor registrados.', AlertType.success);
      this.dialogRef.close(resultado);
    });
  }

  fecharComAprendizadoPendente(): void {
    if (!this.aprendizadoPendente || this.isRepetindoAprendizado) return;
    this.dialogRef.close(this.aprendizadoPendente.resultado);
  }

  /** Envia o aprendizado de cada item; marca os que deram certo e devolve os que falharam. */
  private enviarAprendizado(itens: ItemAssociacao[]): Observable<ItemAssociacao[]> {
    if (!itens.length) return of([]);
    return forkJoin(itens.map(x => this.enviarCodigoFornecedor(x).pipe(map(ok => ({ item: x, ok }))))).pipe(
      map(resultados => {
        resultados.filter(r => r.ok).forEach(r => {
          r.item.isCodigoFornecedorRegistrado = true;
          r.item.decisaoCodigo = 'código registrado';
        });
        return resultados.filter(r => !r.ok).map(r => r.item);
      }),
    );
  }

  /**
   * Kanban: com a associação salva mas incompleta, explica que a nota segue Em lançamento na gaveta e não pode
   * ser vinculada ao pedido. Para continuar no modal, recarrega as versões da nota e dos itens, porque o salvar
   * as atualiza e o próximo envio seria recusado como alteração de outro usuário.
   */
  private fecharSalvoNoKanban(): void {
    const quantidadeLoteSerie = this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isLoteSeriePendente(x)).length;
    const quantidadeFator = this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isFatorPendente(x)).length;
    const pendencias = [
      !this.isTodosAssociados && `${this.quantidadeResolvidos} de ${this.quantidadeTotal} itens associados`,
      quantidadeLoteSerie && `${quantidadeLoteSerie} ${quantidadeLoteSerie === 1 ? 'item' : 'itens'} sem lote/série`,
      quantidadeFator && `${quantidadeFator} ${quantidadeFator === 1 ? 'item' : 'itens'} com o fator de conversão a confirmar`,
    ].filter(Boolean);

    if (!pendencias.length) {
      this.alertService.show('Sucesso', 'Associação salva.', AlertType.success);
      this.dialogRef.close({ salvo: true, confirmado: false, possuiDivergenciaBloqueante: false });
      return;
    }

    this.atualizarVersoes();
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Salvo, mas a associação não foi concluída',
        innerHtml: `${pendencias.join(' · ')}<br><br>A nota fica Em lançamento na gaveta do processo de compras e ainda não pode ser `
          + 'vinculada ao pedido de compra. Você pode retomar a associação por lá quando quiser.',
        buttonConfirm: 'Voltar ao processo de compras',
        buttonClose: 'Continuar resolvendo',
      },
    }).afterClosed().subscribe(resultado => {
      if (resultado?.confirm) this.dialogRef.close({ salvo: true, confirmado: false, possuiDivergenciaBloqueante: false });
    });
  }

  /**
   * Depois de gravar sem fechar o modal: traz as versões novas da nota e dos itens (o próximo envio
   * seria recusado como alteração de outro usuário) e zera as alterações já gravadas.
   */
  private atualizarVersoes(): void {
    this.itens.forEach(x => (x.isAlterado = false));
    this.temAlteracoes = false;
    forkJoin({
      nota: this.nfeEntradaService.obterPorId(this.data.idNFeEntrada),
      itens: this.nfeEntradaItemService.listar(this.data.idNFeEntrada),
    }).pipe(catchError(() => of(null))).subscribe(response => {
      if (!response || response.nota.isError || response.itens.isError) {
        this.alertService.show('Aviso', 'A associação foi salva, mas não foi possível recarregar a nota. Feche e abra o modal antes de alterar de novo.', AlertType.warning);
        return;
      }
      this.datetimeAlteracaoNFeEntrada = response.nota.nfeEntrada.datetimeAlteracao;
      response.itens.listaItem.forEach(salvo => {
        const i = this.itens.find(x => x.item.idNFeEntradaItem === salvo.idNFeEntradaItem);
        if (!i) return;
        i.item.datetimeAlteracao = salvo.datetimeAlteracao;
        i.item.idProduto = salvo.idProduto;
      });
    });
  }

  private montarRequest(): NFeEntradaAssociacaoSalvarRequest {
    const request = new NFeEntradaAssociacaoSalvarRequest();
    request.idNFeEntrada = this.data.idNFeEntrada;
    request.datetimeAlteracaoNFeEntrada = this.datetimeAlteracaoNFeEntrada;
    request.itens = this.itens
      .filter(x => x.isAlterado && x.item.idNFeEntradaItem)
      .map(x => {
        const item = new NFeEntradaAssociacaoItemRequest();
        item.idNFeEntradaItem = x.item.idNFeEntradaItem!;
        item.idProduto = x.produto?.idProduto ?? null;
        item.isProdutoRemovido = !x.produto && !!x.item.idProduto;
        item.idRegraCorrespondenciaProdutoNFeEntrada = x.produto ? x.regra : RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia;
        item.idEstadoAssociacaoItemNFeEntrada = x.produto && x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado
          ? EstadoAssociacaoItemNFeEntradaEnum.Associado
          : EstadoAssociacaoItemNFeEntradaEnum.Pendente;
        item.fatorConversao = x.fator && x.fator > 0 ? x.fator : null;
        item.isFatorConversaoConfirmado = x.isFatorConfirmado && item.fatorConversao !== null;
        item.isLotesNaUnidadeComercial = x.isLotesNaUnidadeComercial;
        item.datetimeAlteracao = x.item.datetimeAlteracao;
        item.lotes = x.lotes;
        item.numerosSerie = x.numerosSerie;
        return item;
      });
    return request;
  }

  // ---------------------------------------------------------------- carga e regras de correspondência

  carregar(): void {
    this.isLoading = true;
    this.isErroCarga = false;
    forkJoin({
      nota: this.nfeEntradaService.obterPorId(this.data.idNFeEntrada),
      itens: this.nfeEntradaItemService.listar(this.data.idNFeEntrada),
    }).pipe(catchError(() => of(null))).subscribe(response => {
      if (!response || response.nota.isError || response.itens.isError) {
        this.isLoading = false;
        this.isErroCarga = true;
        this.alertService.show('Aviso', response?.nota.errorDescription || response?.itens.errorDescription
          || 'Não foi possível carregar a nota.', AlertType.warning);
        return;
      }
      const nfe = response.nota.nfeEntrada;
      this.nota = nfe;
      this.numeroNota = nfe.numero;
      this.serieNota = nfe.serie;
      this.dataEmissao = nfe.dataEmissao;
      this.valorTotal = nfe.valorTotal;
      this.chaveAcesso = nfe.chaveAcesso;
      this.idPessoaEmitente = nfe.idPessoaEmitente;
      this.datetimeAlteracaoNFeEntrada = nfe.datetimeAlteracao;
      this.nomeFornecedor = nfe.emitenteXml?.nome ?? null;
      this.carregarNomeFornecedor();

      // O item de serviço não é associado a produto: fica fora do modal.
      this.itens = response.itens.listaItem
        .filter(item => item.idTipoItemNFeEntrada !== TipoItemNFeEntradaEnum.Servico)
        .map(item => this.montarItem(item));
      this.carregarProdutosAssociados(true);
    });
  }

  private carregarNomeFornecedor(): void {
    if (!this.idPessoaEmitente) return;
    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [this.idPessoaEmitente];
    this.pessoaLookupService.getListPessoaIds(request).pipe(catchError(() => of(null))).subscribe(response => {
      const pessoa = response?.listaPessoa?.[0];
      if (pessoa) this.nomeFornecedor = pessoa.nomeFantasia || pessoa.nome;
    });
  }

  /** Estado vindo do banco: estado, regra, fator confirmado e unidade dos lotes são os gravados. */
  private montarItem(item: NFeEntradaItemStruct): ItemAssociacao {
    const i = new ItemAssociacao();
    i.item = item;
    i.lotes = [...item.lotes];
    i.numerosSerie = [...item.numerosSerie];
    i.isLotesNaUnidadeComercial = item.isLotesNaUnidadeComercial;
    i.regra = item.idRegraCorrespondenciaProdutoNFeEntrada;
    i.estado = item.idEstadoAssociacaoItemNFeEntrada;
    this.montarItensLoteSerie(i);
    return i;
  }

  /**
   * Busca o cadastro dos produtos gravados nos itens. Falha da consulta não vira "sem produto":
   * os ids gravados ficam intactos, salvar e concluir são bloqueados e o usuário pode tentar de
   * novo. Produto que a consulta não devolve não existe mais no cadastro.
   */
  carregarProdutosAssociados(isPrimeiraCarga = false): void {
    const itensComProduto = this.itens.filter(x => x.item.idProduto && !x.produto && !x.isAlterado);
    const ids = [...new Set(itensComProduto.map(x => x.item.idProduto!))];
    const produtos$: Observable<ProdutoListStruct[] | null> = ids.length
      ? this.produtoLookupService.getAllSelectByIds(ids).pipe(map(r => r.isError ? null : r.list ?? []), catchError(() => of(null)))
      : of([]);

    this.isLoading = isPrimeiraCarga;
    this.isErroCargaProdutos = false;
    produtos$.subscribe(produtos => {
      this.isLoading = false;
      if (produtos === null) {
        this.isErroCargaProdutos = true;
      } else {
        itensComProduto.forEach(x => {
          const produto = produtos.find(p => p.idProduto === x.item.idProduto);
          if (!produto) {
            x.isProdutoNaoEncontrado = true;
            x.isExpandido = true;
            return;
          }
          this.aplicarProduto(x, produtoDaLista(produto), x.item.fatorConversao, x.item.isFatorConversaoConfirmado, true);
          x.isExpandido = !this.isResolvido(x);
        });
      }
      if (isPrimeiraCarga) this.itens.filter(x => !x.item.idProduto).forEach(x => this.buscarSugestao(x));
    });
  }

  /**
   * Aplica o produto ao item. Agrupador de variação nunca é o produto final: carrega os SKUs
   * filhos e espera o operador escolher qual recebe a entrada.
   */
  private aplicarProduto(i: ItemAssociacao, produto: ProdutoAssociacaoStruct, fatorSalvo: number | null,
    isFatorSalvoConfirmado: boolean, isVariacao = false): void {
    i.versao++;
    i.decisaoNcm = null;
    i.decisaoEan = null;
    i.decisaoCodigo = null;
    i.isCodigoFornecedorIgnorado = false;
    i.isCodigoFornecedorRegistrado = false;
    if (produto.isAgrupadorVariacao && !isVariacao) {
      i.agrupador = produto;
      i.produto = null;
      i.estado = EstadoAssociacaoItemNFeEntradaEnum.Pendente;
      i.isExpandido = true;
      this.carregarFilhosVariacao(i, produto.idProduto);
      return;
    }
    if (!isVariacao) i.agrupador = null;
    i.produto = produto;
    this.definirFator(i, produto.fatorConversaoFornecedor, fatorSalvo, isFatorSalvoConfirmado);
  }

  private carregarFilhosVariacao(i: ItemAssociacao, idProdutoPai: number): void {
    i.isCarregandoFilhos = true;
    i.isErroFilhos = false;
    this.notaFiscalEntradaService.produtoVariacaoFilhos(idProdutoPai)
      .pipe(catchError(() => of(null)))
      .subscribe(response => {
        if (i.agrupador?.idProduto !== idProdutoPai) return;
        i.isCarregandoFilhos = false;
        if (!response || response.isError) {
          i.isErroFilhos = true;
          return;
        }
        i.filhosVariacao = response.list ?? [];
      });
  }

  private buscarSugestao(i: ItemAssociacao): void {
    i.isBuscandoSugestao = true;
    i.isErroSugestao = false;
    const ordem = this.data.ordemRegras?.length ? this.data.ordemRegras : ORDEM_REGRAS_CORRESPONDENCIA_PADRAO;
    this.tentarRegra(i, this.data.considerarApenasDescricao ? [] : ordem, 0, i.versao);
  }

  /** Falha de consulta interrompe a cadeia do item: "não encontrou" é diferente de "não consultou". */
  private interromperSugestao(i: ItemAssociacao): void {
    i.isBuscandoSugestao = false;
    i.isErroSugestao = true;
    i.isExpandido = true;
  }

  private tentarRegra(i: ItemAssociacao, regras: RegraCorrespondenciaProdutoNFeEntradaEnum[], indice: number, versao: number): void {
    if (i.versao !== versao) return;
    if (indice >= regras.length) {
      this.tentarPorDescricao(i, versao);
      return;
    }

    const regra = regras[indice];
    const proxima = () => this.tentarRegra(i, regras, indice + 1, versao);
    const gtin = somenteDigitos(i.item.codigoEanComercial);
    const codigoFornecedor = i.item.codigoProdutoEmitente?.trim();

    if (regra === RegraCorrespondenciaProdutoNFeEntradaEnum.Ean) {
      if (gtin.length < 8) { proxima(); return; }
      this.notaFiscalEntradaService.produtoPorGtin(gtin).pipe(catchError(() => of(null))).subscribe(resp => {
        if (i.versao !== versao) return;
        if (!resp || resp.isError) { this.interromperSugestao(i); return; }
        if (resp.list?.length === 1) this.aplicarSugestao(i, resp.list[0], regra);
        else proxima();
      });
      return;
    }

    if (regra === RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigo || regra === RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigoDescricao) {
      if (!codigoFornecedor || !this.idPessoaEmitente) { proxima(); return; }
      this.notaFiscalEntradaService.produtoPorCodigoFabricante(codigoFornecedor, this.idPessoaEmitente)
        .pipe(catchError(() => of(null))).subscribe(resp => {
          if (i.versao !== versao) return;
          if (!resp || resp.isError) { this.interromperSugestao(i); return; }
          const candidato = resp.list?.[0];
          const isDescricaoSemelhante = !!candidato && descricoesSemelhantes(candidato.descricao, i.item.descricao);
          if (candidato && (regra === RegraCorrespondenciaProdutoNFeEntradaEnum.FornecedorCodigo || isDescricaoSemelhante))
            this.aplicarSugestao(i, candidato, regra);
          else proxima();
        });
      return;
    }

    if (regra === RegraCorrespondenciaProdutoNFeEntradaEnum.CodigoDescricao) {
      if (!codigoFornecedor) { proxima(); return; }
      this.notaFiscalEntradaService.produtoPorCodigoFabricante(codigoFornecedor, null)
        .pipe(catchError(() => of(null))).subscribe(resp => {
          if (i.versao !== versao) return;
          if (!resp || resp.isError) { this.interromperSugestao(i); return; }
          const candidato = resp.list?.find(p => descricoesSemelhantes(p.descricao, i.item.descricao));
          if (candidato) this.aplicarSugestao(i, candidato, regra);
          else proxima();
        });
      return;
    }

    proxima();
  }

  /** Sugestões por GTIN e fornecedor só confirmam itens sem pendências de associação; ficam para gravar ao salvar. */
  private aplicarSugestao(i: ItemAssociacao, candidato: ProdutoAssociacaoStruct, regra: RegraCorrespondenciaProdutoNFeEntradaEnum): void {
    i.isBuscandoSugestao = false;
    i.regra = regra;
    this.aplicarProduto(i, candidato, null, false);
    this.marcarAlterado(i, false);
    if (i.agrupador) return;
    i.estado = this.podeConfirmar(i) ? EstadoAssociacaoItemNFeEntradaEnum.Associado : EstadoAssociacaoItemNFeEntradaEnum.Pendente;
    i.isExpandido = !this.isResolvido(i);
  }

  private tentarPorDescricao(i: ItemAssociacao, versao: number): void {
    this.selectPesquisavelService.getSelectValues<ProdutoListStruct>(this.urlProduto, i.item.descricao)
      .pipe(catchError(() => of(null)))
      .subscribe(porDescricao => {
        if (i.versao !== versao) return;
        if (!porDescricao) { this.interromperSugestao(i); return; }
        i.isBuscandoSugestao = false;
        i.isExpandido = true;
        if (!porDescricao.length) {
          i.regra = RegraCorrespondenciaProdutoNFeEntradaEnum.SemCorrespondencia;
          return;
        }
        i.regra = RegraCorrespondenciaProdutoNFeEntradaEnum.Descricao;
        this.aplicarProduto(i, produtoDaLista(porDescricao[0]), null, false);
        this.marcarAlterado(i, false);
      });
  }

  // ---------------------------------------------------------------- moldura do modal

  alternarTamanho(): void {
    this.isMaximizado = !this.isMaximizado;
    if (this.isMaximizado) this.dialogRef.addPanelClass('associacao-dialog-maximizado');
    else this.dialogRef.removePanelClass('associacao-dialog-maximizado');
    this.dialogRef.updateSize(
      this.isMaximizado ? '100vw' : 'min(1080px, 94vw)',
      this.isMaximizado ? '100vh' : '88vh');
    // Aberto a 100vw o overlay fica ancorado no canto (flex-start); o updateSize do CDK 21 não
    // reaplica a estratégia de posição, então sem isto o modal restaurado vai para o canto superior esquerdo.
    this.dialogRef.updatePosition();
    // Clique deixa o mat-icon-button com foco (e a sombra cinza). Solta o foco para o state layer
    // sumir; teclado continua com :focus-visible.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  acessarNota(): void {
    const url = `${window.location.origin}/suprimentos/nota-fiscal-entrada/documento/${this.data.idNFeEntrada}`;
    window.open(url, '_blank', 'noopener');
  }

  cancelar(): void {
    if (this.aprendizadoPendente) {
      this.fecharComAprendizadoPendente();
      return;
    }
    if (this.isKanban && !this.data.isSomenteLeitura && !this.isLoading) {
      const quantidadeFator = this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isFatorPendente(x)).length;
      const quantidadeLoteSerie = this.itens.filter(x => x.estado === EstadoAssociacaoItemNFeEntradaEnum.Associado && this.isLoteSeriePendente(x)).length;
      if (this.temAlteracoes || !this.isTodosAssociados || quantidadeFator || quantidadeLoteSerie) {
        const detalhe = [
          quantidadeFator && `${quantidadeFator} ${quantidadeFator === 1 ? 'item está' : 'itens estão'} com o fator de conversão não confirmado`,
          quantidadeLoteSerie && `${quantidadeLoteSerie} ${quantidadeLoteSerie === 1 ? 'item ainda está' : 'itens ainda estão'} sem lote/série`,
        ].filter(Boolean).join(' e ') || 'A associação ainda não foi concluída';
        this.dialog.open(ConfirmModalComponent, {
          data: {
            title: 'Sair sem concluir a associação?',
            description: `${detalhe}. Saindo agora, a divergência não é gerada na nota - ela apenas continua Em lançamento, `
              + 'na gaveta do processo de compras. E enquanto a associação não for concluída, a nota não pode ser vinculada '
              + 'ao pedido de compra.' + (this.temAlteracoes ? ' As alterações não salvas serão descartadas.' : ''),
            buttonConfirm: 'Sair mesmo assim',
            buttonClose: 'Continuar resolvendo',
          },
        }).afterClosed().subscribe(resultado => {
          if (resultado?.confirm) this.dialogRef.close();
        });
        return;
      }
    }

    if (!this.temAlteracoes) {
      this.dialogRef.close();
      return;
    }

    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Descartar alterações da associação?',
        description: 'As associações ainda não salvas serão descartadas.',
        buttonConfirm: 'Descartar',
        buttonClose: 'Continuar editando',
      },
    }).afterClosed().subscribe(resultado => {
      if (resultado?.confirm) this.dialogRef.close();
    });
  }
}
